import { useState, useEffect } from 'react';
import { Outlet, useLocation, Link as RouterLink } from 'react-router-dom';
import { Menu, Bell, Home, ChevronRight, Building2, ChevronDown, Globe } from 'lucide-react';
import Sidebar from './Sidebar';
import SimulationBanner from './SimulationBanner';
import UpgradeModal from './UpgradeModal';
import { useOrganization } from '../contexts/OrganizationContext';
import { useSimulation } from '../contexts/SimulationContext';
import { useInactivityLogout } from '../hooks/useInactivityLogout';
import { useAuth } from '../contexts/AuthContext';
import { useSedeView, type ActiveSede } from '../contexts/SedeViewContext';
import { sedeService } from '../services/sedeService';
import { useDepartmentLabel } from '../hooks/useDepartmentLabel';

// Rutas donde el selector "Ver como" (sede) no aplica:
//  - /sedes: gestión de sedes
//  - /dashboard: el Centro de Comando Global usa su propio filtro de eventos
//  - /users: los usuarios de panel son a nivel de sistema, no de sede
const SEDE_SELECTOR_HIDDEN_ROUTES = ['/sedes', '/dashboard', '/users'];

// Rutas del bloque Cowork / Corporativo — siempre requieren una sede seleccionada
const COWORK_ROUTES = [
  '/corporate-dashboard',
  '/departments',
  '/employees',
  '/parking',
  '/visitor-appointments',
  '/credentials',
  '/access-groups',
  '/spaces',
  '/bulk-import',
  '/reports',
];

const Layout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sedeDropdownOpen, setSedeDropdownOpen] = useState(false);
  const [sedes, setSedes] = useState<{ id: number; name: string }[]>([]);
  const location = useLocation();
  const { organization } = useOrganization();
  const { isSimulating } = useSimulation();
  const { user } = useAuth();
  const { activeSede, setActiveSede } = useSedeView();
  const departmentLabel = useDepartmentLabel();

  useInactivityLogout();

  const isOrgAdmin = user?.isOrgAdmin ?? user?.sedeId == null;
  const isCoworkRoute = COWORK_ROUTES.some(r => location.pathname.startsWith(r));

  useEffect(() => {
    if (!isOrgAdmin) return;
    sedeService.getSedes({}).then(res => {
      setSedes((res.data || []).map((s: any) => ({ id: s.id, name: s.name })));
    }).catch(() => {});
  }, [isOrgAdmin]);

  // En rutas cowork: auto-seleccionar la primera sede si no hay ninguna activa
  useEffect(() => {
    if (!isCoworkRoute || !isOrgAdmin || activeSede || sedes.length === 0) return;
    setActiveSede(sedes[0] as ActiveSede);
  }, [isCoworkRoute, isOrgAdmin, activeSede, sedes, setActiveSede]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const pathnames = location.pathname.split('/').filter((x) => x);

  const breadcrumbNameMap: Record<string, string> = {
    dashboard: 'Dashboard',
    users: 'Usuarios',
    'end-users': 'Usuarios Finales',
    roles: 'Roles',
    controllers: 'Controladoras',
    'access-points': 'Puntos de Acceso',
    logs: 'Logs',
    'operational-dashboard': 'Dashboard NOC',
    'technical-reports': 'Reportes Tecnicos',
    'corporate-dashboard': 'Dashboard Corporativo',
    departments: departmentLabel.plural,
    employees: 'Empleados',
    'visitor-appointments': 'Citas de Visitantes',
    sedes: 'Sedes',
    credentials: 'Credenciales',
    'access-groups': 'Grupos de Acceso',
    spaces: 'Espacios y Reservas',
    'bulk-import': 'Carga Masiva',
    reports: 'Reportes',
    parking: 'Parking',
    profile: 'Mi Perfil',
    preferences: 'Preferencias',
  };

  return (
    <div className="flex h-screen bg-slate-50 dark:bg-slate-950 font-sans text-slate-900 dark:text-slate-100 overflow-hidden">
      {/* Simulation Banner */}
      <SimulationBanner />

      {/* Modal de upgrade de plan */}
      <UpgradeModal />

      {/* Sidebar */}
      <Sidebar mobileOpen={mobileOpen} onMobileClose={handleDrawerToggle} />

      {/* Main content wrapper */}
      <div
        className="flex-1 min-w-0 flex flex-col md:ml-64 transition-all duration-300"
        style={{
          marginTop: isSimulating ? 48 : 0,
        }}
      >
        {/* Header */}
        <header className="h-16 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 flex items-center px-4 md:px-8 shrink-0 relative z-50">
          {/* Mobile menu button */}
          <button
            onClick={handleDrawerToggle}
            className="md:hidden p-2 text-slate-500 hover:text-slate-700 mr-3"
            aria-label="open menu"
          >
            <Menu size={24} />
          </button>

          {/* Breadcrumbs */}
          <div className="flex-1 min-w-0">
            {organization && (
              <p className="text-xs text-slate-400 mb-0.5 truncate">{organization.name}</p>
            )}
            <nav className="flex items-center gap-1 text-sm">
              <RouterLink
                to="/dashboard"
                className="flex items-center gap-1 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <Home size={14} />
                <span>Inicio</span>
              </RouterLink>
              {pathnames.map((value, index) => {
                const last = index === pathnames.length - 1;
                const to = `/${pathnames.slice(0, index + 1).join('/')}`;
                const label = breadcrumbNameMap[value] || value;

                return (
                  <span
                    key={to}
                    className={`items-center gap-1 ${last ? 'hidden md:flex' : 'flex'}`}
                  >
                    <ChevronRight size={14} className="text-slate-300" />
                    {last ? (
                      <span className="text-slate-700 font-medium truncate">{label}</span>
                    ) : (
                      <RouterLink
                        to={to}
                        className="text-slate-400 hover:text-slate-600 transition-colors"
                      >
                        {label}
                      </RouterLink>
                    )}
                  </span>
                );
              })}
            </nav>
          </div>

          {/* Selector "Ver como" — solo org admins con múltiples sedes.
              Oculto en las rutas de SEDE_SELECTOR_HIDDEN_ROUTES. */}
          {isOrgAdmin && sedes.length > 0
            && !SEDE_SELECTOR_HIDDEN_ROUTES.some(r => location.pathname.startsWith(r)) && (
            <div className="relative mr-2">
              <button
                onClick={() => setSedeDropdownOpen(v => !v)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                  activeSede
                    ? 'bg-blue-50 border-blue-300 text-blue-700'
                    : 'bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {activeSede ? <Building2 size={14} /> : <Globe size={14} />}
                <span className="max-w-[120px] truncate">
                  {activeSede ? activeSede.name : 'Todas las sedes'}
                </span>
                <ChevronDown size={13} />
              </button>

              {sedeDropdownOpen && (
                <>
                  <div
                    className="fixed inset-0 z-20"
                    onClick={() => setSedeDropdownOpen(false)}
                  />
                  <div className="absolute right-0 mt-1 w-52 bg-white border border-slate-200 rounded-xl shadow-lg z-30 py-1 overflow-hidden">
                    {!isCoworkRoute && (
                      <>
                        <button
                          onClick={() => { setActiveSede(null); setSedeDropdownOpen(false); }}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 transition-colors ${
                            !activeSede ? 'font-semibold text-blue-700 bg-blue-50' : 'text-slate-700'
                          }`}
                        >
                          <Globe size={14} className="shrink-0" />
                          Todas las sedes
                        </button>
                        <div className="border-t border-slate-100 my-1" />
                      </>
                    )}
                    {sedes.map(s => (
                      <button
                        key={s.id}
                        onClick={() => { setActiveSede(s as ActiveSede); setSedeDropdownOpen(false); }}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 transition-colors ${
                          activeSede?.id === s.id ? 'font-semibold text-blue-700 bg-blue-50' : 'text-slate-700'
                        }`}
                      >
                        <Building2 size={14} className="shrink-0" />
                        {s.name}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Right side actions */}
          <button className="p-2 text-slate-400 hover:text-slate-600 transition-colors">
            <Bell size={20} />
          </button>
        </header>

        {/* Main content */}
        <main className="flex-1 overflow-y-auto bg-slate-50 dark:bg-slate-950 p-4 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
